<script lang="ts">
  import type {
    DeparturesResponseStore,
    DeparturesState,
    SelectedStop,
  } from '../../stores/departures-response.svelte';
  import type { FavoritesStore } from '../../stores/favorites.svelte';
  import EmptyState from '../empty-state.svelte';
  import DepartureGroup from './departure-group.svelte';
  import FavoriteStar from './favorite-star.svelte';
  import FavoritesList from './favorites-list.svelte';
  import type { HafasStopPick } from '../../lib/hafas-stop';
  import HafasStopInput from './hafas-stop-input.svelte';
  import SourceChips from './source-chips.svelte';

  interface Properties {
    departures: DeparturesResponseStore;
    favorites: FavoritesStore;
    /** Fires after a successful board load (autocomplete pick, refresh, or favorite click). Not fired by star toggling or typing. */
    onAfterChange?: () => void;
  }

  const { departures, favorites, onAfterChange }: Properties = $props();

  const inputState = $state({ inputLabel: '', lastSyncedExtId: '' });

  $effect(() => {
    const stop = departures.stop;
    if (!stop) return;
    if (stop.extId === inputState.lastSyncedExtId) return;
    inputState.inputLabel = stop.name;
    inputState.lastSyncedExtId = stop.extId;
  });

  async function loadBoard(stop: SelectedStop): Promise<void> {
    const isApplied = await departures.load(stop);
    if (isApplied) onAfterChange?.();
  }

  function handlePick(stop: HafasStopPick, label: string): void {
    inputState.inputLabel = label;
    inputState.lastSyncedExtId = stop.extId;
    void loadBoard({ extId: stop.extId, name: stop.name, lat: stop.lat, lon: stop.lon });
  }

  function handleFavoriteToggle(): void {
    /* istanbul ignore if -- defensive: the star only renders inside `{#if departures.stop}` */
    if (!departures.stop) return;
    const { extId, name, lat, lon } = departures.stop;
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) return;
    favorites.toggle({ extId, name, lat, lon });
  }

  const isRefreshing = $derived(departures.state === 'loading');

  /** Placeholder for a board with no groups, one line per lifecycle state. */
  const EMPTY_BOARD_MESSAGE: Record<DeparturesState, string> = {
    idle: 'No departures loaded.',
    loading: 'Loading departures…',
    ready: 'No upcoming departures.',
    error: 'No departures loaded.',
  };

  const emptyBoardMessage = $derived(EMPTY_BOARD_MESSAGE[departures.state]);
  /** The error banner already names the failure, so the placeholder stands down. */
  const isEmptyBoardShown = $derived(departures.groups.length === 0 && !departures.errorMessage);

  function refresh(): void {
    /* istanbul ignore if -- defensive: refresh button only renders inside `{#if departures.stop}` */
    if (!departures.stop) return;
    void loadBoard(departures.stop);
  }

  function pickFavorite(favorite: { extId: string; name: string; lat: number; lon: number }): void {
    inputState.inputLabel = favorite.name;
    inputState.lastSyncedExtId = favorite.extId;
    void loadBoard({
      extId: favorite.extId,
      name: favorite.name,
      lat: favorite.lat,
      lon: favorite.lon,
    });
  }
</script>

<!-- HAFAS-driven departures board. Autocomplete → per-stop board with
     source + updated-at chips, a spinning refresh, and a star toggle backed
     by the persistent favorites list. -->
<div class="departures-tab">
  <HafasStopInput
    value={inputState.inputLabel}
    onInput={(value: string) => {
      inputState.inputLabel = value;
    }}
    onPick={handlePick}
  />

  {#if departures.stop}
    <div class="board-header">
      <h2 class="stop-name">{departures.stop.name}</h2>
      <FavoriteStar
        starred={favorites.isStarred(departures.stop.extId)}
        onToggle={handleFavoriteToggle}
      />
      <button
        type="button"
        class="refresh-btn"
        class:spinning={isRefreshing}
        aria-label="Refresh departures"
        disabled={isRefreshing}
        onclick={refresh}>↻</button
      >
    </div>

    <SourceChips
      source={departures.source}
      fetchedAt={departures.fetchedAt}
      state={departures.state}
    />

    {#if departures.state === 'error' && departures.errorMessage}
      <p class="error">{departures.errorMessage}</p>
    {/if}

    {#if isEmptyBoardShown}
      <EmptyState message={emptyBoardMessage} />
    {:else if departures.groups.length > 0}
      {#each departures.groups as group, index (`${String(index)}|${group.label}`)}
        <DepartureGroup {group} />
      {/each}
    {/if}
  {:else}
    <p class="prompt">Search a stop above to see its next departures.</p>
  {/if}

  <FavoritesList items={favorites.items} onPick={pickFavorite} />
</div>

<style>
  .departures-tab {
    display: flex;
    flex-direction: column;
  }

  .board-header {
    display: flex;
    align-items: center;
    gap: var(--space-sm);
    margin-bottom: var(--space-sm);
  }

  .stop-name {
    flex: 1;
    font-size: var(--font-size-lg);
    font-weight: 600;
    margin: 0;
  }

  .refresh-btn {
    background: var(--color-surface);
    border: 1px solid var(--color-border);
    color: var(--color-primary);
    cursor: pointer;
    padding: 2px 8px;
    border-radius: var(--radius-sm);
    font-size: var(--font-size-md);
    line-height: 1;
  }

  .refresh-btn:hover:not(:disabled) {
    border-color: var(--color-primary);
  }

  .refresh-btn.spinning {
    animation: spin 0.8s linear infinite;
  }

  .refresh-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  .error {
    color: var(--color-accent-red);
    font-size: var(--font-size-sm);
    margin-bottom: var(--space-md);
  }

  .prompt {
    color: var(--color-fg-muted);
    font-size: var(--font-size-sm);
    padding: var(--space-md);
    text-align: center;
    margin-bottom: var(--space-md);
  }
</style>
